import { RoutesKeys } from './utils/routes';
import { NgModule } from '@angular/core';
import { RouterModule, Routes, TitleStrategy } from '@angular/router';

import { LoginPage } from './pages/login/login.page';
import { NotFoundPage } from './pages/notfound/notfound.page';
import { OnboardingPage } from './pages/onboarding/onboarding.page';
import { RegisterPage } from './pages/register/register.page';
import { VerificationPage } from './pages/verification/verification.page';
import { ForgotPasswordPage } from './pages/forgot-password/forgot-password.page';
import { CreateNewPasswordPage } from './pages/create-new-password/create-new-password.page';
import { HomePage } from './pages/home/home.page';
import { UserStatusGuard } from './shared/guards/userStatusGuard';
import { CreateNewPasswordGuard } from './shared/guards/create-new-password/create-new-password.guard';
import { BusinessSettingsPage } from './pages/business-settings/business-settings.page';
import { InvitePage } from './pages/invite/invite.page';
import { CreateAccountPage } from './pages/create-account/create-account.page';
import { ProfileCreatedPage } from './pages/profile-created/profile-created.page';
import { InviteExistingPage } from './pages/invite-existing/invite-existing.page';
import { ConfirmedMembershipPage } from './pages/confirmed-membership/confirmed-membership.page';
import { ProductCouponPage } from './pages/product-coupon/product-coupon.page';
import { InviteRefusePage } from './pages/invite-refuse/invite-refuse.page';
import { RewardPage } from './pages/reward/reward.page';
import { AddPointsPage } from './pages/add-points/add-points.page';
import { PointBalancePage } from './pages/point-balance/point-balance.page';
import { RewardsListPage } from './pages/rewards-list/rewards-list.page';
import { CouponsPage } from './pages/coupons/coupons.page';
import { AccountSettingsPage } from './pages/account-settings/account-settings.page';
import { ChangePasswordPage } from './pages/change-password/change-password.page';
import { DeleteAccountPage } from './pages/delete-account/delete-account.page';
import { UnauthenticatedGuard } from './shared/guards/unauthenticated/unauthenticated.guard';
import { AccountDeletedPage } from './pages/account-deleted/account-deleted.page';
import { RepresentativeDeletedGuard } from './shared/guards/representative-deleted/representative-deleted.guard';
import { PageTitleStrategy } from './page-title-strategy';
import { ExpiredInvitePage } from './pages/expired-invite/expired-invite.page';
import { ExpiredInviteGuard } from './shared/guards/expired-invite/expired-invite.guard';
import { FaqPage } from './pages/faq/faq.page';
import { PointBalanceListPage } from './pages/point-balance-list/point-balance-list.page';

const routes: Routes = [
  {
    path: RoutesKeys.ONBOARD,
    component: OnboardingPage,
    canActivate: [UnauthenticatedGuard],
  },
  {
    path: RoutesKeys.LOGIN,
    component: LoginPage,
    canActivate: [UnauthenticatedGuard],
  },
  {
    path: '',
    redirectTo: `/${RoutesKeys.ONBOARD}`,
    pathMatch: 'full',
  },
  {
    path: RoutesKeys.REGISTER,
    component: RegisterPage,
    canActivate: [UnauthenticatedGuard],
  },
  {
    path: RoutesKeys.VERIFICATION,
    component: VerificationPage,
  },
  {
    path: RoutesKeys.FORGOT_PASSWORD,
    component: ForgotPasswordPage,
  },
  {
    path: RoutesKeys.CREATE_NEW_PASSWORD,
    component: CreateNewPasswordPage,
    canActivate: [CreateNewPasswordGuard],
  },
  {
    path: RoutesKeys.HOME,
    component: HomePage,
    canActivate: [UserStatusGuard],
  },
  {
    path: RoutesKeys.BUSINESS_SETTINGS,
    component: BusinessSettingsPage,
    canActivate: [UserStatusGuard],
  },
  {
    path: RoutesKeys.INVITE,
    component: InvitePage,
  },
  {
    path: RoutesKeys.INVITE_REFUSE,
    component: InviteRefusePage,
  },
  {
    path: RoutesKeys.CREATE_ACCOUNT,
    component: CreateAccountPage,
  },
  {
    path: RoutesKeys.PROFILE_CREATED,
    component: ProfileCreatedPage,
  },
  {
    path: RoutesKeys.INVITE_EXISTING,
    component: InviteExistingPage,
    canActivate: [UserStatusGuard],
  },
  {
    path: RoutesKeys.MEMBERSHIP_SUCCESS,
    component: ConfirmedMembershipPage,
  },
  {
    path: RoutesKeys.COUPONS + '/:id',
    component: ProductCouponPage,
    canActivate: [UserStatusGuard],
  },
  {
    path: RoutesKeys.COUPONS_VIP + '/:id',
    component: ProductCouponPage,
    canActivate: [UserStatusGuard],
  },
  {
    path: RoutesKeys.REWARDS,
    component: RewardsListPage,
    canActivate: [UserStatusGuard],
  },
  {
    path: RoutesKeys.REWARDS + '/:id',
    component: RewardPage,
    canActivate: [UserStatusGuard],
  },
  {
    path: RoutesKeys.ADD_POINTS,
    component: AddPointsPage,
    canActivate: [UserStatusGuard],
  },
  {
    path: RoutesKeys.POINT_BALANCE,
    component: PointBalancePage,
    canActivate: [UserStatusGuard],
  },
  {
    path: RoutesKeys.POINT_BALANCE_LIST,
    component: PointBalanceListPage,
    canActivate: [UserStatusGuard],
  },
  {
    path: RoutesKeys.COUPONS,
    component: CouponsPage,
    canActivate: [UserStatusGuard],
  },
  {
    path: RoutesKeys.ACCOUNT_SETTINGS,
    component: AccountSettingsPage,
    canActivate: [UserStatusGuard],
  },
  {
    path: RoutesKeys.CHANGE_PASSWORD,
    component: ChangePasswordPage,
    canActivate: [UserStatusGuard],
  },
  {
    path: RoutesKeys.DELETE_ACCOUNT,
    component: DeleteAccountPage,
    canActivate: [UserStatusGuard],
  },
  {
    path: RoutesKeys.ACCOUNT_DELETED,
    component: AccountDeletedPage,
    canActivate: [UserStatusGuard, RepresentativeDeletedGuard],
  },
  {
    path: RoutesKeys.ACCOUNT_MIGRATION,
    loadChildren: () =>
      import('./account-migration/account-migration.module').then(
        (m) => m.AccountMigrationModule
      ),
  },
  {
    path: RoutesKeys.EXPIRED_INVITE,
    component: ExpiredInvitePage,
    canActivate: [ExpiredInviteGuard],
  },
  {
    path: RoutesKeys.FAQ,
    component: FaqPage,
  },
  // Wild Card Route for 404 request
  { path: '**', pathMatch: 'full', component: NotFoundPage },
];

@NgModule({
  imports: [RouterModule.forRoot(routes, { scrollPositionRestoration: 'top' })],
  exports: [RouterModule],
  providers: [
    {
      provide: TitleStrategy,
      useClass: PageTitleStrategy,
    },
  ],
})
/**
   TODO: JSDoc
**/
export class AppRoutingModule {}
